import React, { useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Button } from '../../../../Components/MUIs';
import BottomSheet from '../../../../Components/Ui/BottomSheet';
import Canvas from '../../../../Components/Canvas/Canvas';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { PermitActions } from '../../../../Reducer/PermitReducer';

/**
 * SignBottomSheet
 *
 * signMode
 *   'default'  : 기존 작성/승인 흐름 (baseInfo.mst.status로 gubun 결정)
 *   'witness'  : 입회 확인 서명 (gubun 없음, mode='witness' 설정)
 *
 * Props
 *   Save1            : 서명 완료 시 호출할 저장 함수
 *   signatureCanvas  : canvas ref
 *   onComplete       : 저장 성공 후 콜백 (optional)
 *   signMode         : 'default' | 'witness'  (default: 'default')
 */
export default function SignBottomSheet({
  onComplete,
  Save1,
  signatureCanvas,
  signMode = 'default',
  initialImageUrl = '',
}) {
  const dispatch = useDispatch();
  const { baseInfo } = useSelector((state) => state.permit);
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const [signInputs, setSignInputs] = useState({
    mapUrl: initialImageUrl,
    mapFiles: {},
  });

  // ── 헤더 텍스트 ─────────────────────────────────────────────────────────
  const renderHeaderText = () => {
    if (signMode === 'witness') {
      return (
        <>
          입회 확인을 <span style={{ color: '#57B84D' }}>완료</span>합니다.
        </>
      );
    }

    if (
      baseInfo?.mst?.status === '승인요청' ||
      baseInfo?.mst?.status === '결재요청' ||
      baseInfo?.mst?.status === '연장요청'
    ) {
      return (
        <>
          {baseInfo?.mst?.reqNm}님의 작업허가서를&nbsp;
          <span style={{ color: '#57B84D' }}>승인</span>합니다.
        </>
      );
    }

    return (
      <>
        작업허가서를 <span style={{ color: '#57B84D' }}>&nbsp;요청</span>합니다.
      </>
    );
  };

  // ── 완료 핸들러 ─────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!signInputs.mapUrl) {
      dispatch(
        showSnackbar({ message: '서명을 완료해주세요.', type: 'warning' })
      );
      return;
    }

    try {
      if (signMode === 'witness') {
        // 입회 모드: gubun 없이 서명 파일 + dataURL 전달
        dispatch(PermitActions.setMode('witness'));
        await Save1({ signFile: signInputs.mapFiles, signUrl: signInputs.mapUrl });
      } else {
        // 기본 모드: status에서 gubun 결정
        let gubun = '';
        if (baseInfo?.mst?.status === '승인요청') gubun = '2';
        else if (baseInfo?.mst?.status === '결재요청') gubun = '1';

        if (gubun === '2') dispatch(PermitActions.setMode('signSm'));
        else if (gubun === '1') dispatch(PermitActions.setMode('signDep'));
        else dispatch(PermitActions.setMode('write'));

        await Save1({ gubun, signFile: signInputs.mapFiles });
      }

      dispatch(BottomSheetActions.CloseBottomSheet());

      if (typeof onComplete === 'function') {
        onComplete();
      } else {
        dispatch(PermitActions.setMode(''));
      }
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      dispatch(
        showSnackbar({ message: '저장 중 오류가 발생했습니다.', type: 'error' })
      );
    }
  };

  // ── 렌더 ────────────────────────────────────────────────────────────────
  return (
    <BottomSheet
      isHeaderChildren={true}
      headerText={''}
      headerChildren={
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            display: 'flex',
            flexWrap: 'wrap',
            lineHeight: 1.4,
          }}
        >
          {renderHeaderText()}
        </Typography>
      }
    >
      <Box sx={{ px: 1 }}>
        <Typography style={{ color: '#918A8A', paddingBottom: '0.5rem' }}>
          서명해주세요.
        </Typography>

        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
              서명
            </Typography>
            <Chip
              sx={{ borderRadius: '8px' }}
              label="재설정"
              size="small"
              variant="outlined"
              onClick={() => {
                signatureCanvas.current.clear();
                setSignInputs({ mapUrl: '', mapFiles: {} });
              }}
            />
          </Box>

          <Box sx={{ pt: 1 }}>
            <Canvas
              signatureCanvas={signatureCanvas}
              userId={userInfo.userNm}
              imageUrl={signInputs.mapUrl}
              onEnd={() => {
                const dataURL = signatureCanvas.current.toDataURL('image/png');
                const decodedData = atob(dataURL.split(',')[1]);
                const byteNumbers = new Array(decodedData.length);
                for (let i = 0; i < decodedData.length; i++) {
                  byteNumbers[i] = decodedData.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'image/png' });

                setSignInputs({
                  mapUrl: dataURL,
                  mapFiles: new File(
                    [blob],
                    `${new Date()
                      .toISOString()
                      .replace(/T/, '')
                      .replace(/\..+/, '')
                      .replace(/-/g, '')
                      .replace(/:/g, '')}.png`,
                    { type: 'image/png' }
                  ),
                });
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        p={1}
        bgcolor="#fff"
        sx={{
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button
          name="완료"
          sx={{
            width: '100%',
            height: 36,
            borderRadius: '5px',
            backgroundColor: '#57B84D',
            color: '#fff',
            boxShadow: 'none',
          }}
          onClick={handleComplete}
        />
      </Box>
    </BottomSheet>
  );
}
